import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Filter, Calendar } from "lucide-react";
import { CHART_YEARS } from "../mock";
import { fetchResults } from "../lib/api";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function ChartPage({ kind = "madhur", title }) {
  const [params, setParams] = useSearchParams();
  const year = params.get("year") || String(CHART_YEARS[0]);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchResults(kind, { year })
      .then((data) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [kind, year]);

  // Sort time strings like "10:05 AM", "1:35 PM" in reverse chronological order (latest first)
  const sortByTime = (arr) => {
    return [...arr].sort((a, b) => {
      const toMinutes = (t) => {
        const match = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!match) return 0;
        let [, hour, min, period] = match;
        hour = parseInt(hour, 10);
        min = parseInt(min, 10);
        if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
        if (period.toUpperCase() === "AM" && hour === 12) hour = 0;
        return hour * 60 + min;
      };
      return toMinutes(b.time) - toMinutes(a.time);
    });
  };

  const filtered = useMemo(() => {
    if (!query) return rows;
    const q = query.toLowerCase();
    return rows.filter(
      (r) => r.date.includes(q) || r.time.toLowerCase().includes(q) || r.number.includes(q)
    );
  }, [rows, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      if (!map.has(r.date)) map.set(r.date, []);
      map.get(r.date).push(r);
    });
    // sort items within group by time
    const arr = Array.from(map.entries());
    arr.sort((a, b) => (a[0] < b[0] ? 1 : -1));
    arr.forEach(([, items]) => {
      const sorted = sortByTime(items);
      items.length = 0;
      items.push(...sorted);
    });
    return arr;
  }, [filtered]);

  const setYear = (y) => {
    const next = new URLSearchParams(params);
    next.set("year", y);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-amber-300">
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </Link>

      <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.2em] font-semibold">
            <Calendar className="w-4 h-4" /> Chart Archive
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-bold text-slate-100">{title} {year}</h1>
          <p className="mt-2 text-slate-400 max-w-2xl text-sm">
            Yearly archive of {title.toLowerCase()} with date, time and number. Filter karein ya search box me
            date / time / number daal kar quick result paayein.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[140px] bg-[#11172a] border-white/10 text-slate-200">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent className="bg-[#11172a] border-white/10 text-slate-200">
              {CHART_YEARS.map((y) => (
                <SelectItem key={y} value={String(y)} className="focus:bg-amber-400/10 focus:text-amber-200">
                  Year {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search date / number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 w-[220px] bg-[#11172a] border-white/10 text-slate-200 placeholder:text-slate-500 focus-visible:ring-amber-400/40"
            />
          </div>
        </div>
      </div>

      <div className="mt-8 space-y-5">
        {loading && (
          <div className="text-center py-16 rounded-xl border border-white/5 bg-[#11172a] text-slate-400">
            Loading…
          </div>
        )}
        {!loading && grouped.length === 0 && (
          <div className="text-center py-16 rounded-xl border border-white/5 bg-[#11172a] text-slate-400">
            No records found for {year}. Admin can add results from the admin panel.
          </div>
        )}
        {grouped.map(([date, items]) => (
          <div key={date} className="rounded-2xl border border-white/5 bg-[#11172a] overflow-hidden">
            <div className="px-5 py-3 bg-[#0e1424] border-b border-white/5 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-200 font-mono">{date}</div>
              <span className="text-[11px] uppercase tracking-wider text-slate-500">{items.length} draws</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 p-4">
              {items.map((r, i) => (
                <div
                  key={r.id || i}
                  className="flex flex-col items-center justify-center rounded-lg bg-white/[0.03] border border-white/5 px-2 py-2.5 hover:border-amber-400/30 transition-colors"
                >
                  <span className="text-[10px] text-slate-500 font-mono">{r.time}</span>
                  <span className="font-mono text-base font-bold text-amber-300 mt-0.5">{r.number}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
